import { logger } from '@/core/logger';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useAppStore } from '@/core/stores/appStore';
import { useCallback, useState } from 'react';
import { ChangePasswordError, ChangePasswordRequest, profileService } from '../services/profileService';

export interface UseChangePasswordResult {
    changePassword: (currentPassword: string, newPassword: string, confirmPassword: string) => Promise<boolean>;
    loading: boolean;
    error: string | null;
    clearError: () => void;
}

function readChangePasswordErrorData(err: unknown): ChangePasswordError | null {
    if (typeof err !== 'object' || err === null || !('response' in err)) {
        return null;
    }
    const response = (err as { response?: { data?: ChangePasswordError } }).response;
    return response?.data ?? null;
}

function readChangePasswordErrorMessage(err: unknown): string {
    const errorData = readChangePasswordErrorData(err);
    if (!errorData) {
        return err instanceof Error ? err.message : 'Failed to change password';
    }
    if (errorData.error) {
        return errorData.error;
    }
    if (errorData.message) {
        return errorData.message;
    }
    const firstError = errorData.errors?.[0];
    if (firstError) {
        return firstError.message || 'Validation failed';
    }
    return 'Failed to change password';
}

/**
 * Hook for changing user password
 * Handles API call and error management
 */
export const useChangePassword = (): UseChangePasswordResult => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const user = useAppStore((state) => state.auth.user);

    const changePassword = useCallback(
        async (currentPassword: string, newPassword: string, confirmPassword: string): Promise<boolean> => {
            if (!user?.primary_email) {
                setError('User email not found');
                logger.error('useChangePassword', 'User email not found');
                return false;
            }

            if (newPassword !== confirmPassword) {
                setError('New password and confirm password do not match');
                logger.error('useChangePassword', 'Password mismatch');
                return false;
            }

            if (currentPassword === newPassword) {
                setError('New password and current password cannot be the same');
                logger.error('useChangePassword', 'New password same as current');
                return false;
            }

            setLoading(true);
            setError(null);

            try {
                const request: ChangePasswordRequest = {
                    email: user.primary_email,
                    current_password: currentPassword,
                    new_password: newPassword,
                    new_password_confirm: confirmPassword,
                };

                logger.info('useChangePassword', 'Changing password', { email: user.primary_email });
                const response = await profileService.changePassword(request);
                logger.info('useChangePassword', 'Password changed successfully', { message: response.message });
                
                trackDynatraceEvent({
                    p_screen: 'change-password',
                    p_actionName: 'Password Change Success',
                    eventType: 'action',
                });
                return true;
            } catch (err: unknown) {
                logger.error('useChangePassword', 'Failed to change password', err);
                setError(readChangePasswordErrorMessage(err));

                trackCatalogEvent({
                    screen: DT_SCREEN.CHANGE_PASSWORD,
                    actionName: 'password_change_failed',
                    eventType: 'error',
                    properties: { p_failedMessage: readChangePasswordErrorMessage(err) },
                });

                return false;
            } finally {
                setLoading(false);
            }
        },
        [user?.primary_email]
    );

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        changePassword,
        loading,
        error,
        clearError,
    };
};

